/**
 * Video Slider Web Component
 * Handles slide navigation, scroll detection, and video pause on navigate.
 * Supports two layouts: "slider" (horizontal scroll) and "list" (stacked with sidebar).
 */
class VideoSlider extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('.video-slider__track');
    this.slides = this.querySelectorAll('.video-slider__slide');
    this.prevButton = this.querySelector('.video-slider__arrow--prev');
    this.nextButton = this.querySelector('.video-slider__arrow--next');
    this.dots = this.querySelectorAll('.video-slider__dot');
    this.listItems = this.querySelectorAll('.video-slider__list-item');
    this.liveRegion = this.querySelector('.video-slider__live-region');

    this.layout = this.dataset.layout || 'slider';
    this.slideCount = this.slides.length;
    this.currentIndex = 0;

    /* Track which slides have been played */
    this.played = new Set();

    if (this.slideCount > 1) {
      this.bindEvents();

      if (this.layout === 'slider') {
        this.updateArrowStates();
        this.updateDots();
      }
    }
  }

  bindEvents() {
    if (this.layout === 'slider') {
      this.prevButton?.addEventListener('click', () => this.prev());
      this.nextButton?.addEventListener('click', () => this.next());

      /* Detect scroll-snap position changes */
      this.track?.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });

      /* Dot clicks */
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = parseInt(dot.dataset.index, 10);
          this.goToSlide(index);
        });
      });
    }

    if (this.layout === 'list') {
      /* List item clicks */
      this.listItems.forEach((item) => {
        item.addEventListener('click', () => {
          const index = parseInt(item.dataset.index, 10);
          this.goToSlide(index);
        });
      });

      /* Mark slide as played when its video poster is clicked */
      this.slides.forEach((slide, index) => {
        const poster = slide.querySelector('.deferred-media__poster');
        poster?.addEventListener('click', () => {
          this.played.add(index);
          this.updateListItems();
        });
      });
    }

    /* Keyboard navigation when focused */
    this.addEventListener('keydown', this.handleKeydown.bind(this));

    /* Observe each slide for Vimeo iframe injection */
    this.observeVimeoIframes();

    /* Listen for Vimeo postMessage events */
    window.addEventListener('message', this.handleVimeoMessage.bind(this));
  }

  /**
   * Watch each slide's deferred-media for dynamically injected Vimeo iframes.
   * DeferredMedia clones iframes from <template> on poster click, so we use
   * a MutationObserver to detect the moment the iframe enters the DOM.
   */
  observeVimeoIframes() {
    this.slides.forEach((slide) => {
      const media = slide.querySelector('deferred-media');
      if (!media) return;

      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (node.nodeName === 'IFRAME' && node.classList.contains('js-vimeo')) {
              this.subscribeVimeoFinish(node);
            }
          }
        }
      });

      observer.observe(media, { childNodes: true, subtree: true });
    });
  }

  /**
   * Subscribe to the Vimeo 'finish' event via postMessage.
   * Waits for the iframe to load before sending the subscription message.
   */
  subscribeVimeoFinish(iframe) {
    iframe.addEventListener('load', () => {
      iframe.contentWindow?.postMessage(
        JSON.stringify({ method: 'addEventListener', value: 'finish' }),
        '*'
      );
    });
  }

  /**
   * Handle incoming Vimeo postMessage events.
   * When a 'finish' event arrives from the current slide's iframe, auto-advance.
   */
  handleVimeoMessage(event) {
    if (!event.origin.includes('vimeo.com')) return;

    let data;
    try {
      data = JSON.parse(event.data);
    } catch {
      return;
    }

    if (data.event !== 'finish') return;

    /* Only advance if the finished video belongs to the current slide */
    const currentSlide = this.slides[this.currentIndex];
    const vimeoIframe = currentSlide?.querySelector('iframe.js-vimeo');

    if (vimeoIframe && vimeoIframe.contentWindow === event.source) {
      this.next();
    }
  }

  handleScroll() {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    this.scrollTimeout = setTimeout(() => {
      this.detectCurrentSlide();
    }, 50);
  }

  detectCurrentSlide() {
    if (!this.track || this.slides.length === 0) return;

    const trackRect = this.track.getBoundingClientRect();
    const trackCenter = trackRect.left + trackRect.width / 2;

    let closestIndex = 0;
    let closestDistance = Infinity;

    this.slides.forEach((slide, index) => {
      const slideRect = slide.getBoundingClientRect();
      const slideCenter = slideRect.left + slideRect.width / 2;
      const distance = Math.abs(slideCenter - trackCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    if (closestIndex !== this.currentIndex) {
      this.currentIndex = closestIndex;
      this.updateArrowStates();
      this.updateDots();
      this.announceSlideChange();
    }
  }

  goToSlide(index) {
    if (index < 0 || index >= this.slideCount) return;
    if (index === this.currentIndex) return;

    this.pauseCurrentSlideVideo();

    if (this.layout === 'list') {
      /* List mode: toggle active classes for show/hide */
      this.slides[this.currentIndex]?.classList.remove('video-slider__slide--active');
      this.slides[index]?.classList.add('video-slider__slide--active');
      this.played.add(index);
      this.currentIndex = index;
      this.updateListItems();
      this.announceSlideChange();
    } else {
      /* Slider mode: scroll to slide */
      const targetSlide = this.slides[index];
      if (!targetSlide || !this.track) return;

      targetSlide.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });

      this.currentIndex = index;
      this.updateArrowStates();
      this.updateDots();
      this.announceSlideChange();
    }
  }

  /**
   * Pause any playing video in the current slide only.
   * Uses postMessage to communicate with YouTube/Vimeo iframes.
   */
  pauseCurrentSlideVideo() {
    const currentSlide = this.slides[this.currentIndex];
    if (!currentSlide) return;

    const ytIframe = currentSlide.querySelector('iframe.js-youtube');
    if (ytIframe) {
      ytIframe.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }),
        '*'
      );
    }

    const vimeoIframe = currentSlide.querySelector('iframe.js-vimeo');
    if (vimeoIframe) {
      vimeoIframe.contentWindow?.postMessage(
        JSON.stringify({ method: 'pause' }),
        '*'
      );
    }
  }

  prev() {
    if (this.currentIndex > 0) {
      this.goToSlide(this.currentIndex - 1);
    }
  }

  next() {
    if (this.currentIndex < this.slideCount - 1) {
      this.goToSlide(this.currentIndex + 1);
    }
  }

  updateArrowStates() {
    if (this.prevButton) {
      this.prevButton.disabled = this.currentIndex === 0;
    }
    if (this.nextButton) {
      this.nextButton.disabled = this.currentIndex === this.slideCount - 1;
    }
  }

  updateDots() {
    this.dots.forEach((dot, index) => {
      const isActive = index === this.currentIndex;
      dot.classList.toggle('video-slider__dot--active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  updateListItems() {
    this.listItems.forEach((item, index) => {
      const isActive = index === this.currentIndex;
      item.classList.toggle('video-slider__list-item--active', isActive);
      item.classList.toggle('video-slider__list-item--played', this.played.has(index));
      item.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  announceSlideChange() {
    if (this.liveRegion) {
      this.liveRegion.textContent = `Video ${this.currentIndex + 1} of ${this.slideCount}`;
    }
  }

  handleKeydown(event) {
    if (this.layout === 'list') {
      /* In list mode, up/down arrows navigate the list */
      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          this.prev();
          this.listItems[this.currentIndex]?.focus();
          break;
        case 'ArrowDown':
          event.preventDefault();
          this.next();
          this.listItems[this.currentIndex]?.focus();
          break;
      }
    } else {
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          this.prev();
          break;
        case 'ArrowRight':
          event.preventDefault();
          this.next();
          break;
      }
    }
  }
}

customElements.define('video-slider', VideoSlider);
